import { deriveIdentityKey, mnemonicToSeed, validateMnemonic } from "../index";
import { fromBase64url as bytes, toBase64url as b64 } from "@link/shared/api/passkeys";
import { linkAad, linkContext, linkProof, linkQrSchema, linkPacketSchema, type LinkQr, type LinkPacket } from "@link/shared/api/device-links";

export const linkHash = async (input: Uint8Array) => b64(new Uint8Array(await crypto.subtle.digest("SHA-256", input)));
export async function newLinkKeys() {
  const pair = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, false, ["deriveBits"]);
  return { privateKey: pair.privateKey, publicKey: b64(new Uint8Array(await crypto.subtle.exportKey("raw", pair.publicKey))) };
}
export async function validateLinkQr(value: unknown, origin: string, now = Date.now()): Promise<LinkQr> {
  const qr = linkQrSchema.parse(value);
  if (qr.origin !== origin || qr.expiresAt <= now || qr.expiresAt > now + 305_000 || await linkHash(bytes(qr.secret)) !== qr.secretHash) throw new Error("This device-link code is expired or belongs to another site. Get a new code from Link.");
  await crypto.subtle.importKey("raw", bytes(qr.targetPublicKey), { name: "ECDH", namedCurve: "P-256" }, false, []);
  return qr;
}
export async function linkComparisonCode(qr: LinkQr) {
  const digest = bytes(await linkHash(new TextEncoder().encode(JSON.stringify([...linkContext(qr), qr.secret]))));
  // 48-bit comparison (not a short login secret). The QR carries 256 bits of entropy.
  return Array.from(digest.slice(0, 6), b => b.toString(16).padStart(2, "0")).join("").toUpperCase().match(/.{4}/g)!.join(" ");
}
async function wrappingKey(privateKey: CryptoKey, peer: string, qr: LinkQr) {
  const publicKey = await crypto.subtle.importKey("raw", bytes(peer), { name: "ECDH", namedCurve: "P-256" }, false, []);
  const shared = new Uint8Array(await crypto.subtle.deriveBits({ name: "ECDH", public: publicKey }, privateKey, 256));
  const secret = bytes(qr.secret);
  const input = new Uint8Array(64); input.set(shared); input.set(secret, 32);
  try {
    const hkdf = await crypto.subtle.importKey("raw", input, "HKDF", false, ["deriveKey"]);
    return await crypto.subtle.deriveKey({ name: "HKDF", hash: "SHA-256", salt: bytes(qr.secretHash), info: new TextEncoder().encode(JSON.stringify(linkContext(qr))) }, hkdf, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
  } finally { shared.fill(0); secret.fill(0); input.fill(0); }
}
export async function sealDeviceLink(qr: LinkQr, phrase: string, accountXid: string, username: string): Promise<LinkPacket> {
  await validateLinkQr(qr, qr.origin);
  if (!(await validateMnemonic(phrase)).ok) throw new Error("Invalid recovery material");
  const seed = await mnemonicToSeed(phrase);
  const clear = new TextEncoder().encode(JSON.stringify({ phrase, accountXid, username }));
  try {
    const identity = await deriveIdentityKey(seed);
    const sender = await newLinkKeys();
    const meta = { senderPublicKey: sender.publicKey, accountXid, identityPublicKey: b64(identity.publicKeyRaw), iv: b64(crypto.getRandomValues(new Uint8Array(12))) };
    const key = await wrappingKey(sender.privateKey, qr.targetPublicKey, qr);
    const packet = { ...meta, ciphertext: b64(new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv: bytes(meta.iv), additionalData: linkAad(qr, meta) }, key, clear))) };
    const signature = b64(new Uint8Array(await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, identity.privateKey, linkProof(qr, packet))));
    return { ...packet, signature };
  } finally { seed.fill(0); clear.fill(0); }
}
export async function openDeviceLink(qr: LinkQr, privateKey: CryptoKey, value: unknown) {
  await validateLinkQr(qr, qr.origin);
  const packet = linkPacketSchema.parse(value);
  const identityKey = await crypto.subtle.importKey("raw", bytes(packet.identityPublicKey), { name: "ECDSA", namedCurve: "P-256" }, false, ["verify"]);
  if (!(await crypto.subtle.verify({ name: "ECDSA", hash: "SHA-256" }, identityKey, bytes(packet.signature), linkProof(qr, packet)))) throw new Error("Device-link signature could not be verified");
  const key = await wrappingKey(privateKey, packet.senderPublicKey, qr);
  const clear = new Uint8Array(await crypto.subtle.decrypt({ name: "AES-GCM", iv: bytes(packet.iv), additionalData: linkAad(qr, packet) }, key, bytes(packet.ciphertext)));
  let seed: Uint8Array | undefined;
  try {
    const payload = JSON.parse(new TextDecoder().decode(clear)) as { phrase: string; accountXid: string; username: string };
    if (payload.accountXid !== packet.accountXid || typeof payload.username !== "string" || !/^[a-z0-9_]{3,32}$/.test(payload.username) || !(await validateMnemonic(payload.phrase)).ok) throw new Error("Invalid linked account");
    seed = await mnemonicToSeed(payload.phrase);
    if (b64((await deriveIdentityKey(seed)).publicKeyRaw) !== packet.identityPublicKey) throw new Error("Linked identity mismatch");
    return payload;
  } finally { clear.fill(0); seed?.fill(0); }
}
